import React from "react";
import { t } from "ttag";

import type {
    ContentStreamBlockWithImage,
    ArticleSlidesBlock as ItemValue,
    ArticleSlidesBlockWrapper as Value,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { Themed } from "../../styles/themes";
import { Carousel } from "../Carousel/Carousel";
import { RichTextStreamBlock } from "../RichTextStreamBlock";
import { SKSArrows } from "../SKSCarousel/Arrows";
import { SKSDots } from "../SKSCarousel/Dots";
import { SpacerBlock } from "../SpacerBlock";
import { ArticleSlide } from "./Slide";

import breakpoint from "../../styles/breakpoint.module.scss";
import styles from "./index.module.scss";

const ArticleSlidesBlock = ({ value }: { value: ItemValue }) => {
    const { slides, content, block_label } = value;

    if (!slides || slides.length === 0) return null;

    const slidesVisibleDesktop = slides.length < 4 ? 3 : 3.15;
    const slidesVisibleTablet = slides.length < 3 ? 2 : 2.3;
    const slidesVisibleMobile = slides.length < 2 ? 1 : 1.37;

    // When the carousel scrolls, the last card needs a trailing margin to align;
    // when it doesn't, that margin throws off Carousel's all-visible math.
    // Return 1/0 so CSS applies the margin only when scrolling.
    const carouselScrolls = (visible: number) =>
        slides.length > visible ? 1 : 0;

    const headerBlocks = content?.content ?? [];
    // Preserve the original richtext's settings with just the blocks passed in
    const contentWith = (blocks: ContentStreamBlockWithImage) =>
        content && blocks.length > 0 ? { ...content, content: blocks } : null;
    // CTA gets special treatment in this block, so we break it out
    const proseContent = contentWith(
        headerBlocks.filter((block) => block.type !== "cta_group"),
    );
    const ctaContent = contentWith(
        headerBlocks.filter((block) => block.type === "cta_group"),
    );

    const headingBlockId = content?.content?.find(
        (b) =>
            (b.type === "heading" || b.type === "eyebrow") &&
            b.value.tag !== "p",
    )?.id;

    const carouselLabelProps = headingBlockId
        ? { labelledBy: headingBlockId }
        : { label: block_label || t`Browse` };

    const imageSizes = [
        `(min-width: ${breakpoint.tablet}) ${Math.round(100 / slidesVisibleDesktop)}vw`,
        `(min-width: ${breakpoint.mobile}) ${Math.round(100 / slidesVisibleTablet)}vw`,
        `${Math.round(100 / slidesVisibleMobile)}vw`,
    ].join(", ");

    return (
        <div
            className={styles.layout}
            data-alignment={content?.alignment ?? "center"}
            style={{
                "--slides-visible-desktop": slidesVisibleDesktop,
                "--slides-visible-tablet": slidesVisibleTablet,
                "--slides-visible-mobile": slidesVisibleMobile,
                "--scrolls-desktop": carouselScrolls(slidesVisibleDesktop),
                "--scrolls-tablet": carouselScrolls(slidesVisibleTablet),
                "--scrolls-mobile": carouselScrolls(slidesVisibleMobile),
            }}
        >
            <div className={styles.headingArea}>
                {proseContent && (
                    <RichTextStreamBlock
                        value={proseContent}
                        className={styles.header}
                        hideBodyOnMobile
                    />
                )}
                {ctaContent && (
                    <RichTextStreamBlock
                        value={ctaContent}
                        className={styles.ctaDesktop}
                    />
                )}
            </div>
            <div className={styles.carouselArea}>
                <Carousel
                    {...carouselLabelProps}
                    loop={false}
                    advanceBy="page"
                    scrollContainerProps={{ className: styles.scrollContainer }}
                    slideWrapperProps={{ className: styles.slidesWrapper }}
                    slideContainerProps={{ className: styles.slideItem }}
                    controls={({ allVisible }) => (
                        <SKSArrows
                            allVisible={allVisible}
                            className={styles.arrows}
                        />
                    )}
                    dots={({ allVisible, slideCount, pageCount }) => (
                        <SKSDots
                            allVisible={allVisible}
                            slideCount={slideCount}
                            pageCount={pageCount}
                            className={styles.dots}
                        />
                    )}
                >
                    {slides.map((slide) => (
                        <ArticleSlide
                            key={slide.id}
                            value={slide.value}
                            imageSizes={imageSizes}
                        />
                    ))}
                </Carousel>
            </div>
            {ctaContent && (
                <RichTextStreamBlock
                    value={ctaContent}
                    className={styles.ctaMobile}
                />
            )}
        </div>
    );
};

const components = {
    article_slides: ArticleSlidesBlock,
    spacer: SpacerBlock,
};

export const ArticleSlidesBlockWrapper = ({ value }: { value: Value }) => {
    if (!value.items) return null;
    return (
        <Themed theme={value.theme} tag="section">
            <StreamField components={components} value={value.items} />
        </Themed>
    );
};
